<script setup lang="ts">
// AI 教练浮窗（全站唯一实例，挂载在 App.vue，路由切换不销毁）
//
// 定位（决策记录 U1）：浮窗只是**快捷入口**，完整功能在主界面 /ai/conversation。
// 因此本组件只保留「浮窗壳」：头部（场景标题 / 历史会话 / 新对话 / 在主界面打开 / 收起）
// 与右下角入口气泡；对话区一律交给 AiChatView 渲染——
// 它与主界面用的是同一套组件、同一份 store 状态，所以切到主界面时对话不丢、也不会新建会话。
//
// 拖动/缩放（计划 Task C3 / 决策记录 U3）：几何（left/top/width/height）存在 store 并持久化，
// 本组件只负责「手势 → 新几何 → store」这一条链路，钳制规则与回收全在 store 里（一份规则两处用）：
// - 拖动：头部 pointerdown 进入拖动态（按钮上的按下不算拖动），pointermove 平移，pointerup 落位；
// - 缩放：四角手柄，右下角必可用，最小 320×360、最大不超视口；
// - 还原：双击头部回到默认位置与尺寸；越界的几何由 store + window.resize 拉回可见区。
//
// 响应式（设计文档 §3）：
// - 桌面端（isDesktop）：卡片（默认宽 400px、高 min(70vh, 640px)），可折叠为圆形气泡；
// - 移动端（isMobile）：整屏 t-drawer，输入区 sticky 于底部并用 dvh 避免软键盘遮挡；
//   移动端不启用拖动/缩放（整屏抽屉没有「位置」语义），也不渲染任何手柄，panelRect 被忽略；
// - 模板分支切换，业务逻辑只写一份（与 App.vue / JudgeJobDetail.vue 的改造模式一致）：
//   两个分支共用同一个 store，浮窗收起/展开不影响正在进行的生成。
//
// 层级：z-index 高于 Monaco 编辑器与既有 t-drawer（本项目头部/侧边栏为 1000）。
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import type { AiAction } from "@/apis/ai.ts";
import { useAiCoachStore } from "@/stores/aiCoach.ts";
import type { AiCoachBubblePos, AiCoachPanelRect } from "@/stores/aiCoach.ts";
import {
  aiCoachEntryVersion,
  BuildAiCoachEntryPath,
  GetAiCoachPageEntry,
  GetAiCoachPageTitle,
} from "@/stores/aiCoachEntry.ts";
import { useUserStore } from "@/stores/user.ts";
import { useBreakpoint } from "@/composables/useBreakpoint";
import AiChatView from "@/components/ai/AiChatView.vue";

const store = useAiCoachStore();
const userStore = useUserStore();
const router = useRouter();
const { isMobile } = useBreakpoint();

const isLogin = computed(() => userStore.isLogin());
const sceneTitle = computed(() => store.sceneDisplayTitle);
/** 对话区实例：新对话后把焦点交给它的输入框 */
const chatRef = ref<InstanceType<typeof AiChatView> | null>(null);

/** 桌面上浮窗完全关闭或已折叠时，右下角保留圆形入口气泡（全站入口） */
const showBubble = computed(() => (isMobile.value ? !store.visible : !store.visible || store.collapsed));

/** 桌面端卡片是否可见（移动端走抽屉分支，与它互斥） */
const panelVisible = computed(() => !isMobile.value && store.visible && !store.collapsed);

/**
 * 卡片几何：left/top/width/height 全部显式给值（不用 right/bottom）。
 * store 里存的已经是「钳制过的视口坐标」，这里只负责搬到样式上——
 * 若这里再算一次 right/bottom，两套坐标迟早会对不上（拖动时最容易暴露）。
 */
const panelStyle = computed(() => ({
  left: `${store.panelRect.left}px`,
  top: `${store.panelRect.top}px`,
  width: `${store.panelRect.width}px`,
  height: `${store.panelRect.height}px`,
  right: "auto",
  bottom: "auto",
}));

// 卡片每次出现都回收一次几何：折叠/关闭期间窗口可能被改小，而 resize 事件只在变化那一刻到达
watch(panelVisible, (visible) => {
  if (visible) {
    store.clampPanelRectToViewport();
  }
});

// ── 会话切换下拉（原生实现：项目未使用 t-popup，移动端也不用 tdesign 的 Cell 类组件） ──
const conversationMenuVisible = ref(false);
const conversationMenuRef = ref<HTMLElement | null>(null);

function sceneLabel(key: string) {
  return store.sceneList.find((item) => item.key === key)?.title ?? key;
}

function toggleConversationMenu() {
  conversationMenuVisible.value = !conversationMenuVisible.value;
  if (conversationMenuVisible.value) {
    void store.refreshConversations();
  }
}

async function handleSelectConversation(id: number) {
  conversationMenuVisible.value = false;
  await store.loadConversation(id);
}

// ── 拖动与缩放（Task C3，仅桌面端）──
//
// 一次手势的起点快照：拖动记指针起点与矩形起点；缩放再记下起始宽高。
// 刻意不用 reactive：它只在一次手势的生命周期内使用，且每次 move 都要读写，放 ref 只是徒增开销。
interface PanelGesture {
  startX: number;
  startY: number;
  startRect: AiCoachPanelRect;
}

let panelGesture: PanelGesture | null = null;
/** 手势进行中（模板据此加 is-gesturing，卡片内禁用文本选中） */
const gestureActive = ref(false);
/** 当前缩放的是哪个角（拖动时为 null） */
let panelGestureCorner: PanelCorner | null = null;
/** 缩放手柄的四个角（本轮只做四角；先声明成数组，便于将来补四边中点的把手） */
const panelCorners = ["nw", "ne", "sw", "se"] as const;
type PanelCorner = (typeof panelCorners)[number];

/** 拖动/缩放期间禁用文本选中与触摸滚动：否则拖到文字上会把对话区内容一起选走 */
function beginPanelGesture() {
  gestureActive.value = true;
  document.body.style.userSelect = "none";
  document.body.style.touchAction = "none";
}

function endPanelGesture() {
  gestureActive.value = false;
  document.body.style.userSelect = "";
  document.body.style.touchAction = "";
  panelGesture = null;
  panelGestureCorner = null;
}

/** 头部按下：进入拖动态。按钮/菜单上的按下不算拖动（历史会话、新对话、收起等都要能正常点） */
function handleHeadPointerDown(event: PointerEvent) {
  if (event.button !== 0) {
    return;
  }
  if ((event.target as HTMLElement | null)?.closest(".ai-coach-icon-btn")) {
    return;
  }
  conversationMenuVisible.value = false;
  panelGesture = { startX: event.clientX, startY: event.clientY, startRect: { ...store.panelRect } };
  beginPanelGesture();
  // 捕获指针：拖出浮窗范围后事件仍回到头部，不会因为鼠标离开元素而中断拖动
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
}

/** 拖动中：只改位置，尺寸保持不变（尺寸交给缩放手柄） */
function handleHeadPointerMove(event: PointerEvent) {
  const gesture = panelGesture;
  if (!gesture) {
    return;
  }
  // 每帧都以「手势起点的矩形 + 总位移」重算，不做增量累加：
  // store 给回的是钳制后的结果，累加会把钳制量吃进下一次位移里，越拖越偏
  store.setPanelRect({
    ...gesture.startRect,
    left: gesture.startRect.left + (event.clientX - gesture.startX),
    top: gesture.startRect.top + (event.clientY - gesture.startY),
  });
}

function handleHeadPointerUp(event: PointerEvent) {
  if (!releasePanelGesture(event)) {
    return;
  }
  // 落位时统一取整并再钳制一次：手势过程中的中间值可以不规整，落盘的值必须干净
  store.normalizePanelRect();
}

/** 四角缩放：被拖的角跟随指针，对角固定不动 */
function handleResizePointerDown(event: PointerEvent, corner: PanelCorner) {
  if (event.button !== 0) {
    return;
  }
  panelGesture = { startX: event.clientX, startY: event.clientY, startRect: { ...store.panelRect } };
  panelGestureCorner = corner;
  beginPanelGesture();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
  event.stopPropagation();
}

function handleResizePointerMove(event: PointerEvent) {
  const gesture = panelGesture;
  const corner = panelGestureCorner;
  if (!gesture || !corner) {
    return;
  }
  const start = gesture.startRect;
  const dx = event.clientX - gesture.startX;
  const dy = event.clientY - gesture.startY;
  // 右侧/下侧的角只改尺寸（左上角固定）；左侧/上侧的角还要跟手改位置（右下角固定）
  const width = corner === "ne" || corner === "se" ? start.width + dx : start.width - dx;
  const height = corner === "sw" || corner === "se" ? start.height + dy : start.height - dy;
  const left = corner === "ne" || corner === "se" ? start.left : start.left + (start.width - width);
  const top = corner === "sw" || corner === "se" ? start.top : start.top + (start.height - height);
  // 最小尺寸与视口上限都在 store 的钳制里，这里只给原始几何
  store.setPanelRect({ left, top, width, height });
}

function handleResizePointerUp(event: PointerEvent) {
  if (!releasePanelGesture(event)) {
    return;
  }
  store.normalizePanelRect();
}

/**
 * 结束一次手势：释放指针捕获并复位全局样式。
 * 指针捕获保证「光标移出浮窗甚至移出页面」时的 pointerup 仍回到发起元素，因此正常路径不会漏收；
 * pointercancel（系统打断）、组件卸载也走这里，保证不会留下一个「跟着鼠标跑」的粘滞状态。
 */
function releasePanelGesture(event: PointerEvent): boolean {
  if (!panelGesture) {
    return false;
  }
  const element = event.currentTarget as HTMLElement;
  if (element.hasPointerCapture(event.pointerId)) {
    element.releasePointerCapture(event.pointerId);
  }
  endPanelGesture();
  return true;
}

/** 双击头部：还原默认位置与尺寸（右下角 400 × min(70vh, 640)，与改造前的 CSS 一致） */
function handleHeadDoubleClick(event: MouseEvent) {
  if ((event.target as HTMLElement | null)?.closest(".ai-coach-icon-btn")) {
    return;
  }
  store.resetPanelRect();
}

// ── 入口气泡拖动（Task C5：气泡常驻右下角会挡住页面内容，拖动后记忆位置）──
//
// 与头部拖动同一套 pointer 捕获范式，但多一个「点击 vs 拖动」判定：
// 气泡本身是按钮（点击打开浮窗），位移超过阈值才视为拖动，拖动结束后的 click 必须抑制。
/** 位移超过该阈值（px）才算拖动，否则视为点击 */
const BUBBLE_DRAG_THRESHOLD = 5;

interface BubbleGesture {
  startX: number;
  startY: number;
  /** 按下瞬间气泡的真实位置：贴角态（store.bubblePos 为 null）用 getBoundingClientRect 现取 */
  startPos: AiCoachBubblePos;
}

let bubbleGesture: BubbleGesture | null = null;
/** 本次按压是否已越过拖动阈值（click 处理里据此决定是否打开浮窗） */
let bubbleDragMoved = false;
/** 拖动进行中（模板据此加 is-dragging，给一点视觉反馈） */
const bubbleDragging = ref(false);

/** 气泡样式：拖动过用 store 记忆的绝对位置；没拖动过不写行内样式，交给 CSS 的 right/bottom 贴角 */
const bubbleStyle = computed(() => {
  if (!store.bubblePos) {
    return {};
  }
  return {
    left: `${store.bubblePos.x}px`,
    top: `${store.bubblePos.y}px`,
    right: "auto",
    bottom: "auto",
  };
});

function handleBubblePointerDown(event: PointerEvent) {
  if (event.button !== 0) {
    return;
  }
  // 起点快照取按下瞬间的真实位置：贴角态的坐标由浏览器排版得出，直接读 DOM 最可靠
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  bubbleGesture = { startX: event.clientX, startY: event.clientY, startPos: { x: rect.left, y: rect.top } };
  bubbleDragMoved = false;
  bubbleDragging.value = true;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  // 刻意不 preventDefault：iOS Safari 上取消 pointerdown 会连带吞掉后续 click，气泡就点不开了。
  // 滚动/缩放手势已由气泡 CSS 的 touch-action: none 交还给我们，无需在这里拦截。
}

function handleBubblePointerMove(event: PointerEvent) {
  const gesture = bubbleGesture;
  if (!gesture) {
    return;
  }
  if (
    !bubbleDragMoved &&
    (Math.abs(event.clientX - gesture.startX) > BUBBLE_DRAG_THRESHOLD ||
      Math.abs(event.clientY - gesture.startY) > BUBBLE_DRAG_THRESHOLD)
  ) {
    // 越过阈值才真正进入拖动态：轻微按压抖动不改变位置，也不会把「贴角」切换成绝对定位
    bubbleDragMoved = true;
  }
  if (!bubbleDragMoved) {
    return;
  }
  // 每帧以「起点位置 + 总位移」重算，不做增量累加（与头部拖动一致，钳制量不会被吃进位移里）
  store.setBubblePos({
    x: gesture.startPos.x + (event.clientX - gesture.startX),
    y: gesture.startPos.y + (event.clientY - gesture.startY),
  });
}

function handleBubblePointerUp(event: PointerEvent) {
  const element = event.currentTarget as HTMLElement;
  if (bubbleGesture && element.hasPointerCapture(event.pointerId)) {
    element.releasePointerCapture(event.pointerId);
  }
  bubbleGesture = null;
  bubbleDragging.value = false;
  store.normalizeBubblePos();
}

/** 点击气泡打开浮窗：拖动（位移超阈值）后的 click 不算数，否则拖完松手浮窗会误开 */
function handleBubbleClick() {
  if (bubbleDragMoved) {
    return;
  }
  handleOpenEntry();
}

/** 窗口尺寸变化：把越界的几何拉回可见区（只记忆不回收 = 缩小窗口后浮窗再也找不到） */
function handleWindowResize() {
  store.clampPanelRectToViewport();
  store.clampBubblePosToViewport();
}

function handleClickOutside(event: MouseEvent) {
  if (!conversationMenuVisible.value) {
    return;
  }
  const target = event.target as Node | null;
  if (target && conversationMenuRef.value && !conversationMenuRef.value.contains(target)) {
    conversationMenuVisible.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleWindowResize);
  // 持久化的几何来自上次会话，视口可能已经变小（如从大屏换到小屏）：进页面就回收一次
  store.clampPanelRectToViewport();
  store.clampBubblePosToViewport();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  // 卸载时清理 window 监听，避免泄漏（浮窗挂在 App.vue 上，正常不会卸载，但热更新与登出会）
  window.removeEventListener("resize", handleWindowResize);
  endPanelGesture();
  // 气泡手势一并复位，避免留下「跟着鼠标跑」的粘滞状态
  bubbleGesture = null;
  bubbleDragging.value = false;
});

// ── 页面漂移检测 ──
//
// 浮窗全站常驻，用户可能在题目 A 打开教练后切到题目 B / 提交页 / 任意页面：
// 此时 store 里的场景与绑定对象仍是 A 的，继续追问会串对象。
// 监听路由变化，把「当前路由注册的教练入口」交给 store 判定是否漂移；
// 漂移时对话区（AiChatView）的上下文条更新说明（基于哪个页面、当前在哪个页面），
// 并提供轻量的「改用当前页面」入口，不弹整条警告。

/** 当前路由的 name（symbol 等异常形态归一为空） */
function currentRouteName(): string {
  const name = router.currentRoute.value.name;
  return typeof name === "string" ? name : "";
}

/** 统一触发一次漂移判定：路由变化与注册表变化（页面数据重载完成）都会走到这里 */
function refreshPageDrift() {
  store.detectPageDrift(GetAiCoachPageEntry(currentRouteName()));
}

// 监听 fullPath 而不是 name：同一路由下参数也会变（题目 A → 题目 B 都是 problem-detail），
// 此时注册表已被页面重新注册为新对象，必须重新判定漂移
watch(
  () => router.currentRoute.value.fullPath,
  () => refreshPageDrift(),
  { immediate: true },
);

// 注册表版本变化（页面重新加载数据后注册了新对象 / 注销）：fullPath 可能没变，需要补一次判定
watch(aiCoachEntryVersion, () => refreshPageDrift());

// ── 交互 ──

/** 点入口气泡：恢复上次的会话（场景与引用没变，不会重置）；打开页面随当前路由上报（统计口径） */
function handleOpenEntry() {
  const routeKey = currentRouteName();
  void store.open({
    autoFocus: true,
    entryPage: routeKey,
    entryPageTitle: routeKey ? GetAiCoachPageTitle(routeKey) : "",
    entryPath: BuildAiCoachEntryPath(router.currentRoute.value),
  });
}

async function handleNewConversation() {
  conversationMenuVisible.value = false;
  store.reset();
  await nextTick();
  void chatRef.value?.focusInput();
}

/** 动作卡片处理完毕：移动端整屏浮窗会挡住跳转后的页面，这里顺带关闭 */
function handleActionHandled(action: AiAction) {
  if (action.type === "judge_goto" && isMobile.value) {
    store.close();
  }
}

/**
 * 「在主界面打开」：带着当前会话跳到主界面（地址即 /ai/conversation/:id，主界面据 :id 选中同一条会话），
 * 随后收起浮窗。还没发出第一条消息时会话在服务端还不存在（id 为 0），退回不带 id 的主界面地址。
 * 桌面端沿用头部「收起为气泡」的语义（setCollapsed），让用户还能从气泡随时回到浮窗；
 * 移动端是整屏抽屉、会盖住刚跳过去的主界面，必须真正关闭（close）。
 */
function handleOpenInMain() {
  if (store.conversationId > 0) {
    void router.push({ name: "ai-conversation-detail", params: { id: String(store.conversationId) } });
  } else {
    void router.push({ name: "ai-conversation" });
  }
  if (isMobile.value) {
    store.close();
  } else {
    store.setCollapsed(true);
  }
}
</script>

<template>
  <!-- 未登录不渲染：/ai 系列接口都要求登录态 -->
  <Teleport v-if="isLogin" to="body">
    <!-- 移动端：整屏抽屉 -->
    <t-drawer
      v-if="isMobile"
      class="ai-coach-drawer"
      :visible="store.visible"
      placement="right"
      size="100%"
      :z-index="3300"
      :header="false"
      :footer="false"
      :close-on-overlay-click="true"
      @close="store.close()"
    >
      <div class="ai-coach-sheet">
        <!-- 头部：场景标题 / 会话切换 / 新建 / 在主界面打开 / 关闭 -->
        <div class="ai-coach-head">
          <div class="ai-coach-head-title">
            <t-icon name="chat" />
            <span>{{ sceneTitle }}</span>
          </div>
          <div class="ai-coach-head-actions">
            <div ref="conversationMenuRef" class="ai-coach-conv">
              <button class="ai-coach-icon-btn" title="历史会话" @click="toggleConversationMenu">
                <t-icon name="history" />
              </button>
              <div v-if="conversationMenuVisible" class="ai-coach-conv-menu">
                <div class="ai-coach-conv-head">
                  <span>最近会话</span>
                  <t-button size="small" variant="text" theme="primary" @click="handleNewConversation">新对话</t-button>
                </div>
                <div v-if="store.conversations.length === 0" class="ai-coach-conv-empty">暂无历史会话</div>
                <button
                  v-for="item in store.conversations"
                  :key="item.id"
                  class="ai-coach-conv-item"
                  :class="{ 'is-active': item.id === store.conversationId }"
                  @click="handleSelectConversation(item.id)"
                >
                  <span class="ai-coach-conv-title">{{ item.title || "未命名会话" }}</span>
                  <span class="ai-coach-conv-meta">{{ sceneLabel(item.scene) }} · {{ item.message_count }} 条</span>
                </button>
              </div>
            </div>
            <button class="ai-coach-icon-btn" title="在主界面打开" @click="handleOpenInMain"><t-icon name="jump" /></button>
            <button class="ai-coach-icon-btn" title="新对话" @click="handleNewConversation"><t-icon name="add" /></button>
            <button class="ai-coach-icon-btn" title="关闭" @click="store.close()"><t-icon name="close" /></button>
          </div>
        </div>

        <AiChatView ref="chatRef" variant="panel" @handled="handleActionHandled" />
      </div>
    </t-drawer>

    <!-- 桌面端：可拖动/缩放的卡片（几何来自 store.panelRect，位置尺寸都写死在行内样式上） -->
    <div
      v-else-if="store.visible && !store.collapsed"
      class="ai-coach-card"
      :class="{ 'is-gesturing': gestureActive }"
      :style="panelStyle"
    >
      <!-- 头部：场景标题 / 会话切换 / 新建 / 在主界面打开 / 折叠（按住可拖动，双击还原） -->
      <div
        class="ai-coach-head"
        title="按住可拖动，双击还原位置与大小"
        @pointerdown="handleHeadPointerDown"
        @pointermove="handleHeadPointerMove"
        @pointerup="handleHeadPointerUp"
        @pointercancel="handleHeadPointerUp"
        @dblclick="handleHeadDoubleClick"
      >
        <div class="ai-coach-head-title">
          <t-icon name="chat" />
          <span>{{ sceneTitle }}</span>
        </div>
        <div class="ai-coach-head-actions">
          <div ref="conversationMenuRef" class="ai-coach-conv">
            <button class="ai-coach-icon-btn" title="历史会话" @click="toggleConversationMenu">
              <t-icon name="history" />
            </button>
            <div v-if="conversationMenuVisible" class="ai-coach-conv-menu">
              <div class="ai-coach-conv-head">
                <span>最近会话</span>
                <t-button size="small" variant="text" theme="primary" @click="handleNewConversation">新对话</t-button>
              </div>
              <div v-if="store.conversations.length === 0" class="ai-coach-conv-empty">暂无历史会话</div>
              <button
                v-for="item in store.conversations"
                :key="item.id"
                class="ai-coach-conv-item"
                :class="{ 'is-active': item.id === store.conversationId }"
                @click="handleSelectConversation(item.id)"
              >
                <span class="ai-coach-conv-title">{{ item.title || "未命名会话" }}</span>
                <span class="ai-coach-conv-meta">{{ sceneLabel(item.scene) }} · {{ item.message_count }} 条</span>
              </button>
            </div>
          </div>
          <button class="ai-coach-icon-btn" title="在主界面打开" @click="handleOpenInMain"><t-icon name="jump" /></button>
          <button class="ai-coach-icon-btn" title="新对话" @click="handleNewConversation"><t-icon name="add" /></button>
          <button class="ai-coach-icon-btn" title="收起为气泡" @click="store.setCollapsed(true)"><t-icon name="chevron-down" /></button>
        </div>
      </div>

      <AiChatView ref="chatRef" variant="panel" @handled="handleActionHandled" />

      <!-- 四角缩放手柄：右下角是主用手柄，另外三角便于从任意方向调整 -->
      <span
        v-for="corner in panelCorners"
        :key="corner"
        class="ai-coach-resize"
        :class="`is-${corner}`"
        :title="corner === 'se' ? '拖动可调整浮窗大小' : ''"
        @pointerdown="handleResizePointerDown($event, corner)"
        @pointermove="handleResizePointerMove"
        @pointerup="handleResizePointerUp"
        @pointercancel="handleResizePointerUp"
        @dblclick.stop="store.resetPanelRect()"
      ></span>
    </div>

    <!-- 入口气泡：桌面端常驻右下角，移动端为固定悬浮按钮；支持拖动挪位（防止挡住页面内容） -->
    <button
      v-if="showBubble"
      class="ai-coach-bubble"
      :class="{ 'is-dragging': bubbleDragging }"
      :style="bubbleStyle"
      :title="store.generating ? 'AI 教练正在生成' : '打开 AI 教练（按住可拖动）'"
      @pointerdown="handleBubblePointerDown"
      @pointermove="handleBubblePointerMove"
      @pointerup="handleBubblePointerUp"
      @pointercancel="handleBubblePointerUp"
      @click="handleBubbleClick"
    >
      <t-icon name="chat" size="22px" />
      <span v-if="store.generating" class="ai-coach-bubble-dot"></span>
    </button>
  </Teleport>
</template>

<style scoped>
/* 层级说明：高于头部/侧边栏（1000）、Monaco 编辑器工具栏与既有 t-drawer；
   移动端抽屉另由 t-drawer 的 z-index 属性（3300）控制 */
.ai-coach-card,
.ai-coach-bubble {
  position: fixed;
  z-index: 3000;
}

/* 入口气泡：常驻右下角 */
.ai-coach-bubble {
  right: 24px;
}

/* 卡片：位置与尺寸由 store.panelRect 通过行内样式给出（left/top/width/height 全部显式覆盖），
   默认值与拖动前的 CSS 完全一致：右下角 24px、400px 宽、min(70vh, 640px) 高。
   这里刻意不再写 position：上面的 .ai-coach-card 规则已经给了 fixed，
   一条更靠后的 position: relative 会把它覆盖回正常流——元素随即被文档流排版，
   既在页面上多撑出一块空白（文档高度凭空变大、出现多余滚动条），
   left/top 也不再相对视口，浮窗会跑到页面下方甚至首屏之外。 */
.ai-coach-card {
  display: flex;
  flex-direction: column;
  background-color: var(--td-bg-color-container);
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-large);
  box-shadow: var(--td-shadow-3);
  overflow: hidden;
  /* 缩放手柄（.ai-coach-resize 是 position: absolute）的定位锚点：
     fixed 元素本身就是绝对定位后代的包含块，不需要额外的 relative */
}

/* 拖动/缩放期间禁用文本选中（body 上的同款样式防止拖到浮窗外的文字上被选中） */
.ai-coach-card.is-gesturing,
.ai-coach-card.is-gesturing * {
  user-select: none;
}

/* 入口气泡：与既有「回到顶部」（右下 24px / 下 80px）错开纵向位置。
   right/bottom 只是「未拖动过」的默认贴角值；拖动后由行内样式（left/top + right/bottom:auto）接管 */
.ai-coach-bubble {
  bottom: 24px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background-color: var(--td-brand-color);
  color: var(--td-text-color-anti);
  cursor: pointer;
  box-shadow: var(--td-shadow-2);
  /* 拖动支持：pointer 手势会接管移动，触摸设备上按住气泡不应触发页面滚动/缩放 */
  touch-action: none;
  user-select: none;
}

/* 拖动中的视觉反馈：半透明 + 抓手光标，让用户知道「现在在拖」 */
.ai-coach-bubble.is-dragging {
  opacity: 0.85;
  cursor: grabbing;
}

.ai-coach-bubble:hover {
  background-color: var(--td-brand-color-hover);
}

.ai-coach-bubble-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--td-error-color);
}

/* 头部：只放浮窗自己的入口（对话区在 AiChatView 里）；整条头部是拖动热区 */
.ai-coach-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--td-component-border);
  background-color: var(--td-bg-color-container);
  cursor: move;
  /* 触摸设备上按住头部不应触发滚动/缩放手势，否则 pointermove 会被浏览器接管 */
  touch-action: none;
}

/* 头部里的按钮恢复手型指针与文本选中（按钮上的按下不进入拖动态） */
.ai-coach-head .ai-coach-icon-btn,
.ai-coach-head .ai-coach-conv-menu {
  cursor: pointer;
  user-select: none;
}

/* 缩放手柄：只有四角的小方块可点，不用时透明，悬停才显形（避免挡住对话区内容） */
.ai-coach-resize {
  position: absolute;
  width: 14px;
  height: 14px;
  z-index: 3;
  touch-action: none;
}

.ai-coach-resize.is-nw {
  left: 0;
  top: 0;
  cursor: nwse-resize;
}

.ai-coach-resize.is-ne {
  right: 0;
  top: 0;
  cursor: nesw-resize;
}

.ai-coach-resize.is-sw {
  left: 0;
  bottom: 0;
  cursor: nesw-resize;
}

.ai-coach-resize.is-se {
  right: 0;
  bottom: 0;
  cursor: nwse-resize;
}

/* 右下角主手柄：常态就给出视觉暗示（两段斜线），其余三角悬停显形 */
.ai-coach-resize.is-se {
  background-image: linear-gradient(
    135deg,
    transparent 0 55%,
    var(--td-text-color-placeholder) 55% 65%,
    transparent 65% 75%,
    var(--td-text-color-placeholder) 75% 85%,
    transparent 85%
  );
  opacity: 0.7;
}

.ai-coach-resize:hover {
  opacity: 1;
  background-color: var(--td-bg-color-container-hover);
  border-radius: var(--td-radius-default);
}

.ai-coach-head-title {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--td-text-color-primary);
}

.ai-coach-head-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-coach-head-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.ai-coach-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: var(--td-radius-default);
  background: transparent;
  color: var(--td-text-color-secondary);
  cursor: pointer;
  font-size: 16px;
}

.ai-coach-icon-btn:hover {
  background-color: var(--td-bg-color-container-hover);
  color: var(--td-brand-color);
}

/* 会话切换下拉 */
.ai-coach-conv {
  position: relative;
}

.ai-coach-conv-menu {
  position: absolute;
  top: 32px;
  right: 0;
  z-index: 10;
  width: 240px;
  /* 常态上限 280px；视口很矮（窗口压扁）时以视口兜底，避免菜单底部被卡片 overflow:hidden 裁掉 */
  max-height: min(280px, calc(100dvh - 96px));
  overflow-y: auto;
  padding: 6px;
  background-color: var(--td-bg-color-container);
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-default);
  box-shadow: var(--td-shadow-2);
}

.ai-coach-conv-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 6px 6px;
  font-size: 12px;
  color: var(--td-text-color-secondary);
}

.ai-coach-conv-empty {
  padding: 12px 6px;
  text-align: center;
  font-size: 12px;
  color: var(--td-text-color-placeholder);
}

.ai-coach-conv-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: var(--td-radius-default);
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.ai-coach-conv-item:hover,
.ai-coach-conv-item.is-active {
  background-color: var(--td-bg-color-container-hover);
}

.ai-coach-conv-title {
  font-size: 13px;
  color: var(--td-text-color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-coach-conv-meta {
  font-size: 12px;
  color: var(--td-text-color-placeholder);
}

/* 移动端：整屏 sheet。抽屉头/尾已关闭，正文区交给 sheet 撑满 */
.ai-coach-sheet {
  display: flex;
  flex-direction: column;
  /* dvh 而非 vh：软键盘弹出时视口高度会变，用 vh 会把输入区顶出屏幕；
     max-height 兜底（抽屉正文有内边距时会比视口略矮） */
  height: 100dvh;
  max-height: 100%;
}

/* 窄屏兜底：低于断点时卡片本身也不该出现（由 v-if 分支保证，这里只防风控） */
@media (max-width: 800px) {
  .ai-coach-card {
    display: none;
  }
}
</style>

<!--
  全局样式（刻意不加 scoped）：t-drawer 的正文由 tdesign 组件自身渲染并被 Teleport 到 body，
  这部分 DOM 不带本组件的 scope 属性，scoped / :deep() 都命中不了。
  类名 ai-coach-drawer 只在本组件使用，因此这条规则不会影响其它抽屉。
-->
<style>
.ai-coach-drawer .t-drawer__body {
  /* 去掉正文内边距，让整屏 sheet 真正铺满 */
  padding: 0;
}
</style>
